import { yupResolver } from '@hookform/resolvers/yup'
import { CountryData } from 'country-codes-list'
import { find, map } from 'lodash-es'
import { UploadIcon } from 'lucide-react'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { getTranslation } from '../../../../account/services/auth'
import Loader from '../../../../common/components/Loader'
import {
  GeneratedTicket,
  GeneratedTicketList,
  GeneratedTicketPayload,
  Languages,
} from '../../../../common/models'
import { TicketCategory, TicketList } from '../../../../common/models/ticket'
import FormGroup from '../../../../core/components/FormGroup'
import {
  createTicket,
  getAllGeneratedTickets,
  getGeneratedTicketsZip,
  getYourTicketsPdf,
  uploadTicketsCsv,
} from '../../../../core/services/generate-tickets'
import {
  getAllCategories,
  getTicketsByCategoryId,
} from '../../../../core/services/tickets'
import translate from '../../../../core/services/translate'
import { MessageBox } from '../../messages/MessageBox'
import { PageTitle } from '@/components'
import Spinner from '@/components/Spinner'
import { Button, Card, Checkbox, Input, Modal } from '@/components/ui'
import { PhoneNumberCountry } from '@/components/phone-number-country'
import SelectField from '@/components/select-field'
import { cn } from '@/lib/utils'

export interface IGenerateTicketValues {
  ticketLanguage: string
  ticketId: string
  ticketCategory?: string
  ticketPrice: number
  personalData: boolean
  uploadCsv: boolean
  numberOfTickets: number
  firstName?: string
  lastName?: string
  email?: string
  phoneNumberCountry?: {
    countryCode: string
    countryCodeValue: string
    phone?: string
  }
  label?: string | null
  pdfLink?: string | null
  csvFile?: File
}

export interface GenerateTicketModalProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  editMode: boolean
  page: number
  size: number
  setTotalElemenets: (elements: number) => void
  setGeneratedTickets: (tickets: GeneratedTicketList[]) => void
  generatedTicket?: GeneratedTicket
  setEditMode: (elements: boolean) => void
  setLoading: (l: boolean) => void
  loading: boolean
  currency: string
  allLanguages: Languages[]
  setGeneratedTicket: (ticket: GeneratedTicket) => void
}

export const GenerateTicketModal = (
  props: GenerateTicketModalProps,
): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    page,
    size,
    setTotalElemenets,
    setGeneratedTickets,
    generatedTicket,
    setEditMode,
    editMode,
    setLoading,
    loading,
    currency,
    allLanguages,
    setGeneratedTicket,
  } = props
  const [price, setPrice] = useState<number>(0)
  const [pdfLink, setPdfLink] = useState<string>('')
  const [localAllLanguages, setLocalAllLanguages] = useState<Languages[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [tickets, setTickets] = useState<TicketList[]>([])
  const { festivalName } = useParams()
  const [ticketCategoriesData, setTicketCategoriesData] = useState<
    TicketCategory[]
  >([])
  const [isUploading, setIsUploading] = useState(false)
  const [csvFileName, setCsvFileName] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const language = getTranslation()

  useEffect(() => {
    if (festivalName === 'untold-dubai') {
      setLocalAllLanguages(
        allLanguages.sort((a, b) => {
          if (a.locale < b.locale) {
            return -1
          }
          if (a.locale > b.locale) {
            return 1
          }
          return 0
        }),
      )
    } else {
      setLocalAllLanguages(allLanguages)
    }
  }, [allLanguages])

  const schema: yup.ObjectSchema<IGenerateTicketValues> = yup.object({
    ticketLanguage: yup.string().required(),
    ticketCategory: yup.string().optional(),
    ticketId: yup.string().required('Ticket is required'),
    ticketPrice: yup.number().required(),
    pdfLink: yup.string().optional().nullable(),
    numberOfTickets: yup.number().required('Field required'),
    personalData: yup.boolean().required().default(false),
    uploadCsv: yup.boolean().required().default(false),
    firstName: yup.string().when(['personalData', 'uploadCsv'], {
      is: (personalData: boolean, uploadCsv: boolean) =>
        personalData && !uploadCsv,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    lastName: yup.string().when(['personalData', 'uploadCsv'], {
      is: (personalData: boolean, uploadCsv: boolean) =>
        personalData && !uploadCsv,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    email: yup.string().when(['personalData', 'uploadCsv'], {
      is: (personalData: boolean, uploadCsv: boolean) =>
        personalData && !uploadCsv,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
    label: yup.string().optional().nullable(),
    phoneNumberCountry: yup
      .object()
      .shape({
        countryCode: yup.string().required(),
        countryCodeValue: yup.string().required(),
        phone: yup
          .string()
          .test(
            'Valid phone number',
            translate(language.value, 'common.validPhoneNumber'),
            function (value) {
              const personalData = this.from?.[1]?.value?.personalData
              const uploadCsv = this.from?.[1]?.value?.uploadCsv
              if (personalData && uploadCsv) return true
              if (!personalData && !uploadCsv) return true

              const country: CountryData = this.parent.countryCodeValue
              if (value) return isValidPhoneNumber(`${country}${value}`)
              return false
            },
          )
          .optional(),
      })
      .optional(),
    csvFile: yup.mixed<File>().when('uploadCsv', {
      is: (uploadCsv: boolean) => uploadCsv,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.optional(),
    }),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    setValue,
    setError,
    setFocus,
    control,
    watch,
  } = useForm<IGenerateTicketValues>({
    mode: 'onChange',
    resolver: yupResolver<IGenerateTicketValues>(schema),
    defaultValues: {
      personalData: true,
      ticketLanguage: festivalName === 'untold-dubai' ? 'en' : 'ro',
      label: new Date().valueOf().toString(),
    },
  })
  let category = watch('ticketCategory')
  const ticketIdWatch = watch('ticketId')
  const personalDataWatch = watch('personalData')
  const uploadCsvWatch = watch('uploadCsv')
  const numberOfTicketsWatch = watch('numberOfTickets')

  useEffect(() => {
    if (ticketIdWatch) {
      setPrice(
        (find(tickets, (tic) => tic.id === ticketIdWatch)?.price || 0) *
          numberOfTicketsWatch,
      )
    }
  }, [ticketIdWatch, numberOfTicketsWatch])

  const handleClose = () => {
    setShowCreateModal(false)
    setPrice(0)
    reset()
  }

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      if (generatedTicket) {
        setPdfLink(generatedTicket?.pdfLink)
      }
      const { data: catData, status: catStatus } = await getAllCategories({
        size: 1000,
      })
      if (catStatus === 200) {
        const printCategory = catData.content.filter(
          (ticketCat) => ticketCat.name === 'PRINT',
        )
        category =
          printCategory.length > 0 ? printCategory[0].id : catData.content[0].id
        setTicketCategoriesData(
          printCategory.length > 0 ? printCategory : catData.content,
        )
        await getCategoryTickets()
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }, [generatedTicket])

  useEffect(() => {
    if (showCreateModal) {
      getData()
      setValue('phoneNumberCountry', {
        countryCode: 'RO',
        countryCodeValue: '+40',
        phone: '',
      })
    }
  }, [getData, showCreateModal])

  const getCategoryTickets = useCallback(async () => {
    try {
      if (!category) {
        category = ticketCategoriesData[0].id
      }
      const { data } = await getTicketsByCategoryId(category, {
        size: 1000,
      })
      setTickets(data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [category])

  useEffect(() => {
    setValue(
      'ticketPrice',
      find(tickets, (ticket) => ticket.id === ticketIdWatch)?.price || 0,
    )
  }, [ticketIdWatch])

  useEffect(() => {
    if (!!generatedTicket && editMode) {
      setTimeout(() => {
        setValue(
          'ticketCategory',
          find(
            ticketCategoriesData,
            (categories) => categories.name === generatedTicket.ticketCategory,
          )?.id || '',
        )
        setValue(
          'ticketId',
          find(tickets, (ticket) => ticket.name === generatedTicket.ticketName)
            ?.id || '',
        )
        setValue('ticketPrice', generatedTicket.ticketPrice)
        setValue('ticketLanguage', generatedTicket.locale)
        setValue('numberOfTickets', generatedTicket.numberOfTickets)
        setValue('personalData', generatedTicket.personalData)
        setValue('pdfLink', generatedTicket.pdfLink)
        if (generatedTicket.personalData) {
          setValue('firstName', generatedTicket.firstName)
          setValue('lastName', generatedTicket.lastName)
          setValue('email', generatedTicket.email)
          // Parse phone number if exists

          if (generatedTicket.phoneNumber) {
            const parsed = parsePhoneNumber(generatedTicket.phoneNumber)
            if (parsed) {
              setValue('phoneNumberCountry', {
                countryCode: parsed.country || 'RO', // Default, adjust based on your needs
                countryCodeValue: parsed.countryCallingCode
                  ? `+${parsed.countryCallingCode}`
                  : '+40',
                phone: parsed.nationalNumber,
              })
            }
          }
        } else {
          setValue('label', generatedTicket.label)
        }
      }, 300)
    }
  }, [generatedTicket, ticketCategoriesData, tickets])

  useEffect(() => {
    if (category) {
      getCategoryTickets()
    }
  }, [category])

  useEffect(() => {
    return () => {
      reset()
    }
  }, [showCreateModal])

  const submit = async (values: IGenerateTicketValues) => {
    if (values.uploadCsv) {
      const file = values.csvFile
      if (file) {
        try {
          setLoading(true)
          const { status } = await uploadTicketsCsv({
            ticketId: ticketIdWatch,
            file,
            locale: values.ticketLanguage,
          })

          if (status === 200) {
            setEditMode(true)
            const response = await getAllGeneratedTickets({
              page: page,
              size,
            })
            setGeneratedTickets(response.data.content)
            setTotalElemenets(response.data.totalElements)
          }
        } catch (err) {
          if (err instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(err.message)
            setErrorStatus(
              (err as { response?: { status?: number } })?.response?.status,
            )
          } else {
            console.error(err)
          }
        } finally {
          setLoading(false)
        }
      }
    } else {
      setLoading(true)
      const label = values.label
        ? values.label
        : new Date().valueOf().toString()
      setValue('label', label)
      try {
        const data: GeneratedTicketPayload = {
          ticketId: values.ticketId,
          numberOfTickets: values.numberOfTickets,
          personalData: values.personalData,
          locale: values.ticketLanguage,
        }
        if (values.personalData && values.phoneNumberCountry) {
          data.firstName = values.firstName
          data.lastName = values.lastName
          const rawPhone = values.phoneNumberCountry.phone ?? ''
          const phone = rawPhone.startsWith('0') ? rawPhone.slice(1) : rawPhone
          data.phoneNumber = `${values.phoneNumberCountry.countryCodeValue}${phone}`
          data.email = values.email
        } else {
          data.label = label
        }

        const resp = await createTicket(data)
        const response = await getAllGeneratedTickets({
          page: page,
          size,
        })
        setTotalElemenets(response.data.totalElements)
        if (resp.status === 200 && response.status === 200) {
          setEditMode(true)
          setPdfLink(resp.data)
          setGeneratedTickets(response.data.content)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    }
  }

  console.log(errors)

  return (
    <>
      {loading && <Loader />}
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Generate Tickets" />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2 justify-end border-t border-monochrome-120 pt-4">
            <Button onClick={handleClose} type="button" variant="secondary">
              {editMode ? 'Done' : 'Cancel'}
            </Button>
            {editMode ? (
              <div />
            ) : (
              <Button
                disabled={loading || isUploading}
                variant="primary"
                form="media-photo-form"
                type="submit"
                loading={loading}
              >
                Generate
              </Button>
            )}
          </div>
        }
      >
        <form
          id="media-photo-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup
            label="Ticket Language"
            tooltip="Ticket will be generated in this Language"
            labelClassName="w-full justify-between"
          >
            <Controller
              name="ticketLanguage"
              control={control}
              render={({ field }) => (
                <SelectField
                  ref={field.ref}
                  value={field.value || ''}
                  onValueChange={field.onChange}
                  disabled={editMode}
                  outline
                  placeholder="Select Language"
                  error={errors?.ticketLanguage?.message}
                  options={localAllLanguages.map((lang) => ({
                    label: lang.name,
                    value: lang.locale,
                  }))}
                />
              )}
            />
          </FormGroup>
          <Card className="flex flex-col items-start gap-3 w-full p-3 bg-monochrome-120 border-none">
            <div className="flex flex-row items-start gap-3 w-full">
              <FormGroup
                label="Applied for Categories Tickets"
                tooltip={translate(
                  language.value,
                  'rewards.invites.appliedForTicketsTooltip',
                )}
                labelClassName="w-full justify-between"
              >
                <Controller
                  name="ticketCategory"
                  control={control}
                  render={({ field }) => {
                    return (
                      <SelectField
                        ref={field.ref}
                        value={field.value || ''}
                        onValueChange={field.onChange}
                        outline
                        placeholder="Select Applied for Categories Tickets"
                        error={errors.ticketCategory?.message}
                        disabled={editMode}
                        options={map(ticketCategoriesData, (ticket) => ({
                          label: ticket.name,
                          value: ticket.id,
                        }))}
                      />
                    )
                  }}
                />
              </FormGroup>
              <FormGroup
                label="Select Ticket"
                tooltip="Select Ticket"
                labelClassName="w-full justify-between"
              >
                <Controller
                  name="ticketId"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      outline
                      placeholder="Select Ticket"
                      error={errors.ticketId?.message}
                      disabled={editMode}
                      options={tickets.map((ticket) => ({
                        label: `${ticket.name} - ${ticket.price} ${currency}`,
                        value: ticket.id,
                      }))}
                    />
                  )}
                />
              </FormGroup>
            </div>
            <FormGroup
              label="Number of Tickets (Max 10)"
              labelClassName="w-full justify-between"
            >
              <Input
                type="number"
                min={1}
                disabled={editMode}
                isInvalid={!!errors?.numberOfTickets?.message}
                {...register('numberOfTickets')}
                max={10}
                placeholder="Enter number of tickets"
              />
            </FormGroup>
            <FormGroup label="Price" labelClassName="w-full justify-between">
              <Input type="number" value={price} disabled />
            </FormGroup>
          </Card>
          <div className="space-y-2">
            <FormGroup
              label="Personal Data"
              direction="row"
              labelProps={{
                htmlFor: 'personalData',
              }}
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
            >
              <Controller
                name="personalData"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="personalData"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) => {
                      field.onChange(Boolean(checked))
                      if (!checked) {
                        setValue('uploadCsv', false)
                      }
                    }}
                    disabled={editMode}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Upload CSV"
              direction="row"
              labelProps={{
                htmlFor: 'uploadCsv',
              }}
              labelClassName="whitespace-nowrap"
              inputWrapperClassName="w-auto"
              wrapperClassName="w-auto flex-row-reverse justify-end"
            >
              <Controller
                name="uploadCsv"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id="uploadCsv"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                    disabled={editMode || !personalDataWatch}
                  />
                )}
              />
            </FormGroup>
          </div>
          {!uploadCsvWatch && personalDataWatch && (
            <Card className="flex flex-col items-start gap-3 w-full p-3 bg-monochrome-120 border-none">
              <FormGroup
                label="First Name"
                labelClassName="w-full justify-between"
              >
                <Input
                  isInvalid={!!errors?.firstName?.message}
                  {...register('firstName')}
                  type="text"
                  disabled={editMode}
                  placeholder="Enter First Name"
                />
              </FormGroup>
              <FormGroup
                label="Last Name"
                labelClassName="w-full justify-between"
              >
                <Input
                  isInvalid={!!errors?.lastName?.message}
                  {...register('lastName')}
                  type="text"
                  disabled={editMode}
                  placeholder="Enter Last Name"
                />
              </FormGroup>
              <Controller
                control={control}
                render={({ field }) => (
                  <div className="grid grid-cols-2 gap-2 w-full">
                    <PhoneNumberCountry
                      value={{
                        countryCode: field?.value?.countryCode,
                        countryCodeValue: field?.value?.countryCodeValue,
                        phoneNumber: field?.value?.phone || '',
                      }}
                      onChange={(value) => {
                        field.onChange({
                          countryCode: value.countryCode,
                          countryCodeValue: value.countryCodeValue,
                          phone: value.phoneNumber || '',
                        })
                      }}
                      disabled={editMode}
                      buttonClassName="border border-monochrome-120 !rounded-[16px] h-[40px] items-center px-4 pb-0 text-monochrome-2 bg-monochrome-3-60"
                      labelClassName="text-monochrome-1"
                      textInputProps={{
                        outline: true,
                        prefixClassName: 'left-2',
                        clearClassName: 'right-2',
                        labelClassName: 'text-monochrome-1',
                        className:
                          '!bg-monochrome-3-60 !rounded-[16px] h-[40px] px-4',
                        error: errors.phoneNumberCountry?.phone?.message,
                      }}
                    />
                  </div>
                )}
                name="phoneNumberCountry"
              />
              <FormGroup label="Email" labelClassName="w-full justify-between">
                <Input
                  isInvalid={!!errors?.email?.message}
                  {...register('email')}
                  disabled={editMode}
                  type="text"
                  placeholder="Enter Email"
                />
              </FormGroup>
            </Card>
          )}
          {personalDataWatch && uploadCsvWatch && (
            <div className="flex flex-col gap-3 w-full">
              <div className="flex items-center justify-between">
                <span className="text-sm text-monochrome-1">
                  Upload CSV File (Max 25 entries)
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={editMode}
                  onClick={async () => {
                    window.open(
                      '/assets/csv/Generate-Tickets-Example.csv',
                      '_blank',
                    )
                  }}
                >
                  <UploadIcon className="w-4 h-4" />
                  Download CSV Example
                </Button>
              </div>
              <div
                className={cn(
                  'flex items-center gap-2 w-full rounded-2xl border border-monochrome-120 p-3',
                  errors.csvFile?.message && 'border-accent-3',
                )}
              >
                <Input
                  type="file"
                  accept=".csv"
                  className="hidden"
                  isInvalid={!!errors?.csvFile?.message}
                  disabled={editMode}
                  {...register('csvFile')}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setCsvFileName(file.name)
                      setValue('csvFile', file, {
                        shouldValidate: true,
                      })
                    }
                  }}
                  ref={fileInputRef}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={editMode}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadIcon className="w-4 h-4" />
                </Button>
                <span className="text-sm text-monochrome-2 flex-1">
                  {csvFileName || 'Upload CSV file'}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={editMode || isUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadIcon className="w-4 h-4" />
                  Upload
                </Button>
              </div>
              {errors.csvFile?.message && (
                <span className="text-sm text-accent-3">
                  {errors.csvFile?.message}
                </span>
              )}
            </div>
          )}
          {!uploadCsvWatch && !personalDataWatch && (
            <FormGroup label="Label" labelClassName="w-full justify-between">
              <div className="flex items-center gap-2 w-full relative">
                <Input
                  type="text"
                  disabled={editMode}
                  isInvalid={!!errors?.label?.message}
                  {...register('label')}
                  placeholder="Enter label"
                  className="h-[40px]"
                />
                <Button
                  type="button"
                  variant="primary-bordered"
                  className="absolute right-0 top-0 h-[40px]"
                  disabled={editMode}
                  onClick={() =>
                    setValue('label', new Date().valueOf().toString())
                  }
                >
                  Generate
                </Button>
              </div>
            </FormGroup>
          )}
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
